import styled from "@emotion/styled";
import { useCallback, useRef, useState } from "react";
import z from "zod";

import type * as auth from "@skilitics/auth-client";
import {
  Button,
  Chip,
  FieldText,
  Form,
  FormRowField,
  FormikForm,
  ModalDialog,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { PendingButtons } from "./PendingButtons.tsx";

const validationSchema = z.object({
  email: z.string().email(),
});

const initialValues = { email: "" };

export interface InviteMemberFormProps {
  orgName: string;
  existing: auth.WorkspaceMemberInfo;
  commit: (recipients: auth.InviteRecipient[]) => Promise<void>;
  close: () => void;
}

export const InviteMembersDialog = ({ orgName, existing, commit, close }: InviteMemberFormProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const [recipients, setRecipients] = useState<auth.InviteRecipient[]>([]);

  const add = useCallback((email: string) => {
    if (email) {
      // No way to set role on creation yet.
      // You can modify it in the pending invites list currently.
      setRecipients((old) => [...old, { email, role: "edit" }]);
    }
  }, []);
  const remove = useCallback((index: number) => {
    setRecipients((old) => old.toSpliced(index, 1));
  }, []);

  const fieldRef = useRef<HTMLInputElement | null>(null);

  return (
    <ModalDialog
      open
      maxWidth="xs"
      header={formatMessage("header")}
      buttons={
        <PendingButtons
          confirmLabel={formatMessage("confirmLabel")}
          cancelLabel={formatMessage("cancelLabel")}
          confirmDisabled={!recipients.length}
          confirm={async () => {
            await commit(recipients);
          }}
          close={close}
          autoFocus={false}
        />
      }
      onClose={close}
    >
      <Content>
        <p>{formatMessage("details", { name: orgName })}</p>
        <FormikForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange
          validate={({ email }) => {
            if (recipients.some((recipient) => recipient.email === email)) {
              return { email: formatMessage("alreadyInRecipients") };
            } else if (existing.invites.some((invite) => invite.email === email)) {
              return { email: formatMessage("alreadyInInvites") };
            } else if (existing.members.some((member) => member.email === email)) {
              return { email: formatMessage("alreadyInMembers") };
            }
          }}
          onSubmit={({ email }, helpers) => {
            helpers.resetForm();
            add(email);
          }}
        >
          <Form blurOnEnter={false}>
            <FormRowField name="email">
              <Row>
                <FieldText
                  autoFocus
                  ref={fieldRef}
                  type="email"
                  placeholder={formatMessage("emailPlaceholder")}
                  style={{ flexGrow: 1 }}
                />
                <Button type="submit" variant="contained">
                  Add
                </Button>
              </Row>
            </FormRowField>
          </Form>
        </FormikForm>
        <div style={{ display: "flex", gap: 8, flex: "row wrap" }}>
          {!recipients.length && <Info>No recipients added yet.</Info>}
          {recipients.map((recipient, index) => (
            <Chip key={index} color="secondary" label={recipient.email} onDelete={() => remove(index)} />
          ))}
        </div>
      </Content>
    </ModalDialog>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px;

  font-size: 14px;

  p {
    margin: 0;
  }
`;

const Info = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.generalTextIconVariant};
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
`;

const formatStrings = {
  header: {
    en: "Invite to Workspace",
  },
  details: {
    en:
      "Enter the email addresses of the people you'd like to invite to " +
      "join the workspace: {name}. " +
      "They'll receive an invitation email with access instructions.",
  },
  emailPlaceholder: {
    en: "Email address",
  },
  alreadyInRecipients: {
    en: "This email is already in the list of recipients.",
  },
  alreadyInInvites: {
    en: "An invitation for this email has already been sent",
  },
  alreadyInMembers: {
    en: "There is already a member with this email.",
  },
  cancelLabel: {
    en: "Cancel",
  },
  confirmLabel: {
    en: "Send Invites",
  },
};
